package com.wintermar.inspection.core.domain

import com.apollographql.apollo.api.ApolloResponse
import com.apollographql.apollo.api.Operation
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn

sealed class FlowResult<T>(
    val data: T? = null,
    val error: Throwable? = null,
    val showLoading: Boolean = false
) {
    class Loading<T>(showLoading: Boolean = false) : FlowResult<T>(showLoading = showLoading)
    class Success<T>(data: T?) : FlowResult<T>(data = data)
    class Error<T>(error: Throwable?) : FlowResult<T>(error = error)
}

fun <D : Operation.Data, T> handleAsFlow(
    request: suspend () -> ApolloResponse<D>,
    response: suspend (ApolloResponse<D>) -> T
): Flow<FlowResult<T>> = flow {
    emit(FlowResult.Loading<T>(showLoading = true))
    try {
        val raw = request()
        val errors = raw.errors
        if (!errors.isNullOrEmpty()) {
            val first = errors.firstOrNull()
            val message = first?.message ?: first?.toString() ?: "GraphQL error"
            val code = first?.extensions?.get("code")?.toString() ?: "UNKNOWN"
            throw RuntimeException("$code, $message")
        }

        if (raw.exception?.message != null) {
            val message = raw.exception?.message ?: Constant.UNKNOWN_ERROR
            throw RuntimeException(message)
        }

        val mapped = response(raw)
        emit(FlowResult.Success(mapped))
    } catch (e: Throwable) {
        emit(FlowResult.Error<T>(e))
    }
    emit(FlowResult.Loading<T>(showLoading = false))
}.flowOn(Dispatchers.Default)

fun <T, R> mapResultResourceAsFlow(
    action: suspend () -> Result<T>,
    transform: (T?) -> R
): Flow<FlowResult<R>> = flow {
    when (val result = action()) {
        is Result.Success -> emit(FlowResult.Success(transform(result.data)))
        is Result.Error -> emit(FlowResult.Error(result.error))
    }
}.flowOn(Dispatchers.Default)
